import graphApiSlice from 'src/shared/state/api-slices/graphApiSlice';
import { SampleFilter } from 'src/shared/types';
import { siteSummaryQuery, SiteSummaryQueryResult } from './siteSummaryQuery';
import { siteFilesQuery, SiteFilesQueryResult } from './siteFilesQuery';
import { siteContextQuery, SiteContextQueryResult } from './siteContextQuery';
import {
    siteContextFieldsQuery,
    SiteContextFieldsQueryResult,
    setActivityMutation,
    setStageMutation,
    setObjectivesMutation,
    setAreaMutation,
    setEcologicalContextMutation,
} from './siteContextFieldsQuery';
import { siteKeyMetricsSpeciesQuery, SiteKeyMetricsSpeciesQueryResult } from './siteKeyMetricsSpeciesQuery';
import { siteSpeciesCountsQuery, SiteSpeciesCountsQueryResult } from './siteSpeciesCountsQuery';

type SiteSummaryQueryParams = {
    customerId: string;
    siteId: string;
};

type PlaceholderMutationParams = {
    customerId: string;
    siteId: string;
};

type SiteKeyMetricsSpeciesQueryParams = {
    projectId: string;
    samples?: SampleFilter;
};

const siteSummaryGraphSlice = graphApiSlice.injectEndpoints({
    endpoints: builder => ({
        siteSummary: builder.query<SiteSummaryQueryResult['siteSummary']['getSite'], SiteSummaryQueryParams>({
            query: params => ({
                body: siteSummaryQuery,
                variables: params,
            }),
            transformResponse: response => response.siteSummary.getSite,
        }),
        siteSpeciesCounts: builder.query<SiteSpeciesCountsQueryResult['siteSummary']['getSiteSpeciesCounts'], { siteId: string }>({
            query: params => ({
                body: siteSpeciesCountsQuery,
                variables: params,
            }),
            transformResponse: response => response.siteSummary.getSiteSpeciesCounts,
        }),
        siteSummaryFiles: builder.query<SiteFilesQueryResult['projectDefinition']['files'], { projectId: string }>({
            query: params => ({
                body: siteFilesQuery,
                variables: { projectId: params.projectId },
            }),
            transformResponse: response => response.projectDefinition.files,
        }),
        siteContext: builder.query<SiteContextQueryResult['projectDefinition'], { siteId: string }>({
            query: params => ({
                body: siteContextQuery,
                variables: { siteId: params.siteId },
            }),
            transformResponse: response => response.projectDefinition,
        }),
        siteContextFields: builder.query<SiteContextFieldsQueryResult['siteSummary']['getSiteSummaryFields'], { projectId: string }>({
            query: params => ({
                body: siteContextFieldsQuery,
                variables: { projectId: params.projectId },
            }),
            transformResponse: response => response.siteSummary.getSiteSummaryFields,
            providesTags: (_result, _err, arg) => [{ type: 'site', id: arg.projectId }],
        }),
        siteKeyMetricsSpecies: builder.query<SiteKeyMetricsSpeciesQueryResult['siteKeyMetricsSpecies'], SiteKeyMetricsSpeciesQueryParams>({
            query: params => ({
                body: siteKeyMetricsSpeciesQuery,
                variables: params,
            }),
            transformResponse: response => response.siteKeyMetricsSpecies,
            providesTags: (_result, _err, arg) => [{ type: 'project', id: arg.projectId }],
        }),
        setSiteActivity: builder.mutation<boolean, { projectId: string; activity: string }>({
            query: params => ({
                body: setActivityMutation,
                variables: params,
            }),
            async onQueryStarted(arg, api) {
                const { queryFulfilled, dispatch } = api;
                const patch = dispatch(
                    siteSummaryGraphSlice.util.updateQueryData('siteContextFields', { projectId: arg.projectId }, draft => {
                        if (draft) draft.activity = arg.activity;
                    })
                );
                try {
                    await queryFulfilled;
                } catch (_e) {
                    patch.undo();
                }
            },
            invalidatesTags: (_res, _err, arg) => [{ type: 'site', id: arg.projectId }],
        }),
        setSiteStage: builder.mutation<boolean, { projectId: string; stage: string }>({
            query: params => ({
                body: setStageMutation,
                variables: params,
            }),
            async onQueryStarted(arg, api) {
                const { queryFulfilled, dispatch } = api;
                const patch = dispatch(
                    siteSummaryGraphSlice.util.updateQueryData('siteContextFields', { projectId: arg.projectId }, draft => {
                        if (draft) draft.stage = arg.stage;
                    })
                );
                try {
                    await queryFulfilled;
                } catch (_e) {
                    patch.undo();
                }
            },
            invalidatesTags: (_res, _err, arg) => [{ type: 'site', id: arg.projectId }],
        }),
        setSiteObjectives: builder.mutation<boolean, { projectId: string; objectives: string }>({
            query: params => ({
                body: setObjectivesMutation,
                variables: params,
            }),
            async onQueryStarted(arg, api) {
                const { queryFulfilled, dispatch } = api;
                const patch = dispatch(
                    siteSummaryGraphSlice.util.updateQueryData('siteContextFields', { projectId: arg.projectId }, draft => {
                        if (draft) draft.objectives = arg.objectives;
                    })
                );
                try {
                    await queryFulfilled;
                } catch (_e) {
                    patch.undo();
                }
            },
            invalidatesTags: (_res, _err, arg) => [{ type: 'site', id: arg.projectId }],
        }),
        setSiteArea: builder.mutation<boolean, { projectId: string; area: number }>({
            query: params => ({
                body: setAreaMutation,
                variables: params,
            }),
            async onQueryStarted(arg, api) {
                const { queryFulfilled, dispatch } = api;
                const patch = dispatch(
                    siteSummaryGraphSlice.util.updateQueryData('siteContextFields', { projectId: arg.projectId }, draft => {
                        if (draft) draft.area = arg.area;
                    })
                );
                try {
                    await queryFulfilled;
                } catch (_e) {
                    patch.undo();
                }
            },
            invalidatesTags: (_res, _err, arg) => [{ type: 'site', id: arg.projectId }],
        }),
        setSiteEcologicalContext: builder.mutation<boolean, { projectId: string; ecologicalContext: string }>({
            query: params => ({
                body: setEcologicalContextMutation,
                variables: params,
            }),
            async onQueryStarted(arg, api) {
                const { queryFulfilled, dispatch } = api;
                const patch = dispatch(
                    siteSummaryGraphSlice.util.updateQueryData('siteContextFields', { projectId: arg.projectId }, draft => {
                        if (draft) draft.ecologicalContext = arg.ecologicalContext;
                    })
                );
                try {
                    await queryFulfilled;
                } catch (_e) {
                    patch.undo();
                }
            },
            invalidatesTags: (_res, _err, arg) => [{ type: 'site', id: arg.projectId }],
        }),
        // Placeholder mutation for future updates on site context or notes
        updateSiteNotes: builder.mutation<boolean, PlaceholderMutationParams>({
            query: params => ({
                body: `mutation updateSiteNotes($customerId: String!, $siteId: String!, $notes: String) {\n  portfolioMutations {\n    updateSiteNotes(customerId: $customerId, siteId: $siteId, notes: $notes)\n  }\n}`,
                variables: { ...params, notes: '' },
            }),
        }),
    }),
});

export const {
    useSiteSummaryQuery,
    useSiteSpeciesCountsQuery,
    useSiteSummaryFilesQuery,
    useSiteContextQuery,
    useSiteContextFieldsQuery,
    useSiteKeyMetricsSpeciesQuery,
    useSetSiteActivityMutation,
    useSetSiteStageMutation,
    useSetSiteObjectivesMutation,
    useSetSiteAreaMutation,
    useSetSiteEcologicalContextMutation,
    useUpdateSiteNotesMutation,
} = siteSummaryGraphSlice;

export default siteSummaryGraphSlice.reducer;
